import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Field, NativeSelect } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const endOfYear = () => `${new Date().getFullYear()}-12-31`;
const todayStr = () => new Date().toISOString().slice(0, 10);

export function LicenseRequestDialog({ open, onOpenChange, athlete, onDone }) {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [athletes, setAthletes] = useState([]);
  const [athleteId, setAthleteId] = useState("");
  const [issue, setIssue] = useState(todayStr());
  const [expiry, setExpiry] = useState(endOfYear());
  const [disciplines, setDisciplines] = useState([]);
  const [categories, setCategories] = useState([]);
  const [disc, setDisc] = useState("");
  const [cats, setCats] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDisc(""); setCats([]); setNotes(""); setAthleteId(athlete?.id || ""); setIssue(todayStr()); setExpiry(endOfYear());
    if (!athlete) api.get("/athletes").then((r) => setAthletes(r.data));
    api.get("/disciplines").then((r) => setDisciplines(r.data.filter((d) => d.active)));
    api.get("/categories").then((r) => setCategories(r.data.filter((c) => c.active)));
  }, [open, athlete]);

  const toggle = (id) => setCats(cats.includes(id) ? cats.filter((c) => c !== id) : [...cats, id]);
  const submit = async () => {
    setBusy(true);
    try {
      const payload = { athlete_id: athleteId, discipline_id: disc, category_ids: cats, notes };
      if (isAdmin) {
        const { data } = await api.post("/licenses/issue", { ...payload, issue_date: issue, expiry_date: expiry });
        toast.success(`Licenza ${data.license_number} rilasciata: QR code generato`);
        onOpenChange(false);
        onDone?.(data);
      } else {
        await api.post("/licenses", payload);
        toast.success("Richiesta inoltrata alla sede nazionale");
        onOpenChange(false);
        onDone?.();
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="license-request-dialog">
        <DialogHeader><DialogTitle>{isAdmin ? "Inserisci e rilascia licenza" : "Richiesta nuova licenza"}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">
          {athlete ? `Per ${athlete.last_name} ${athlete.first_name}. ` : ""}
          {isAdmin ? "Inserimento diretto della sede nazionale: la licenza è attiva subito, con numero e QR code." : "La licenza sarà valida solo dopo l'approvazione della sede nazionale."}
        </p>
        {!athlete && (
          <Field label="Atleta">
            <NativeSelect value={athleteId} onChange={setAthleteId} testid="license-request-athlete-select">
              <option value="">Seleziona l'atleta…</option>
              {athletes.map((a) => <option key={a.id} value={a.id}>{a.last_name} {a.first_name} · {a.membership_number}</option>)}
            </NativeSelect>
          </Field>
        )}
        {isAdmin && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Data emissione"><Input type="date" value={issue} onChange={(e) => setIssue(e.target.value)} data-testid="license-issue-date-input" /></Field>
            <Field label="Data scadenza"><Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="license-expiry-date-input" /></Field>
          </div>
        )}
        <Field label="Disciplina sportiva">
          <NativeSelect value={disc} onChange={(v) => { setDisc(v); setCats([]); }} testid="license-request-discipline-select">
            <option value="">Seleziona…</option>
            {disciplines.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </NativeSelect>
        </Field>
        {disc && (
          <div>
            <div className="mb-2 text-sm font-medium text-slate-700">Categorie di abilitazione</div>
            <div className="grid gap-2 sm:grid-cols-2">
              {categories.filter((c) => c.discipline_id === disc).map((c) => (
                <label key={c.id} className={`flex cursor-pointer items-start gap-2 rounded-lg border p-3 text-sm transition-colors ${cats.includes(c.id) ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                  <input type="checkbox" checked={cats.includes(c.id)} onChange={() => toggle(c.id)} className="mt-0.5" data-testid={`license-request-category-${c.code}`} />
                  <span><span className="font-medium text-slate-900">{c.name}</span><span className="block text-xs text-slate-500">{c.description}</span></span>
                </label>
              ))}
            </div>
          </div>
        )}
        <Field label="Note per la sede nazionale"><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="license-request-notes-input" /></Field>
        <DialogFooter>
          <Button onClick={submit} disabled={busy || !disc || cats.length === 0 || !athleteId || (isAdmin && !expiry)} className={isAdmin ? "bg-emerald-600 hover:bg-emerald-700" : "bg-[#0F2042] hover:bg-[#1E3A8A]"} data-testid="license-request-submit-btn">{isAdmin ? "Rilascia licenza" : "Invia richiesta"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
